import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Home, UserPlus, LogIn, Sparkles, Trash2, Pencil, Clock, ShieldCheck } from "lucide-react";
import { API } from "./api";
import { ListingCard } from "./ListingCard";
import EspaceAcquereur from "./EspaceAcquereur";
import { useAuth, authApi } from "./authApi";

const inp = "w-full bg-white/[0.06] border border-white/15 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#C19A6B] transition-colors";
const DRAFT_KEY = "om_acq_draft";
const GOLD = "#C19A6B";

// Expérience Espace Acquéreur (hero immersif + panneau compte + résultats).
// Réutilisée sur la page /espace-acquereurs ET les sites personnalisés (page assistant).
export default function AcquereurExperience({ slug = "", brand = "OFF MARKETTE", postLoginPath }) {
  const { t } = useTranslation();
  const { user, setUser, checkAuth } = useAuth();
  const isAuthed = user && typeof user === "object";
  const [mode, setMode] = useState("account");
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", raw_text: "", anonymous: false });
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState([]);
  const [matches, setMatches] = useState([]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    try {
      const d = sessionStorage.getItem(DRAFT_KEY);
      if (d) { setF((s) => ({ ...s, raw_text: d })); sessionStorage.removeItem(DRAFT_KEY); }
    } catch (_) {}
  }, []);

  const loadSaved = () => { authApi.myAcquereurs().then((r) => setSaved(r || [])).catch(() => {}); };
  const titleFor = (s) => s.title || ("Recherche " + [(s.type_bien || [])[0], (s.secteurs || [])[0], s.budget].filter(Boolean).join(" · ")).trim() || (s.summary || "Ma recherche").split(/[.,;]/)[0].slice(0, 55);
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch (_) { return ""; } };
  const delSaved = async (id) => { if (!window.confirm("Supprimer cette recherche enregistrée ?")) return; try { await authApi.deleteMyAcquereur(id); loadSaved(); } catch (_) {} };
  const renameSaved = async (s) => { const tt = window.prompt("Nom de la recherche :", titleFor(s)); if (tt === null) return; try { await authApi.renameMyAcquereur(s.id, tt.trim()); loadSaved(); } catch (_) {} };

  useEffect(() => {
    if (isAuthed) {
      loadSaved();
      authApi.myMatches().then((r) => {
        const flat = [];
        (r || []).forEach((s) => (s.matches || []).forEach((m) => { if (!flat.find((x) => x.slug === m.slug)) flat.push(m); }));
        flat.sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
        setMatches(flat.slice(0, 6));
      }).catch(() => {});
    }
  }, [isAuthed, done]);

  const googleSignup = () => {
    const path = postLoginPath || (typeof window !== "undefined" ? window.location.pathname : "/espace-acquereurs");
    try { sessionStorage.setItem("om_post_login", path); if (f.raw_text) sessionStorage.setItem(DRAFT_KEY, f.raw_text); } catch (_) {}
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + path;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async () => {
    setErr("");
    if ((f.raw_text || "").trim().length < 10) { setErr(t("acqspace.err_describe")); return; }
    if (!isAuthed) {
      if (mode === "account") {
        if (!f.email) { setErr(t("acqspace.err_email")); return; }
        if ((f.password || "").length < 6) { setErr(t("acqspace.err_password")); return; }
      } else {
        if (!f.first_name || !f.email) { setErr(t("acqspace.err_firstemail")); return; }
      }
    }
    setLoading(true);
    try {
      if (!isAuthed && mode === "account") {
        const u = await authApi.registerUser({ email: f.email, password: f.password, name: `${f.first_name} ${f.last_name}`.trim() });
        setUser(u);
      }
      const res = await fetch(`${API}/acquereurs`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, slug: slug || undefined, anonymous: isAuthed ? false : f.anonymous }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Erreur");
      setDone(d);
      if (checkAuth) checkAuth();
    } catch (e) { setErr(typeof e.message === "string" ? e.message : "Envoi impossible"); }
    setLoading(false);
  };

  const modePill = (m, icon, label) => (
    <button onClick={() => { setMode(m); setErr(""); }} data-testid={`acq-mode-${m}`}
      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider rounded-full border transition-colors ${mode === m ? "text-[#050505] border-transparent" : "border-white/20 text-zinc-300 hover:border-white/40"}`}
      style={mode === m ? { background: GOLD } : {}}>
      {icon} {label}
    </button>
  );

  const AccountPanel = isAuthed ? (
    <div data-testid="acq-connected">
      <div className="flex items-center gap-2.5 rounded-xl border px-3.5 py-3 mb-4" style={{ borderColor: "rgba(193,154,107,0.35)", background: "rgba(193,154,107,0.08)" }}>
        <Sparkles className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
        <p className="text-xs text-zinc-200 leading-snug">{t("acqspace.connected_as", { name: user.name || user.email })} — {t("acqspace.connected_hint")}</p>
      </div>
      {saved.length > 0 && (
        <div data-testid="acq-saved-list">
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">{t("acqspace.saved_title")} <span className="text-zinc-600">({saved.length})</span></p>
          <div className="space-y-2 max-h-[260px] overflow-auto pr-1">
            {saved.map((s) => (
              <div key={s.id} className="rounded-xl border border-white/10 bg-white/[0.04] p-3" data-testid="acq-saved-item">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate" data-testid="acq-saved-title">{titleFor(s)}</p>
                    {s.created_at && <p className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {fmtDate(s.created_at)}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => renameSaved(s)} data-testid={`acq-rename-${s.id}`} title="Renommer" className="p-1.5 rounded-md text-zinc-400 hover:text-[#C19A6B] hover:bg-white/5 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => delSaved(s.id)} data-testid={`acq-delete-${s.id}`} title="Supprimer" className="p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {(s.secteurs || s.type_bien || s.budget) && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {s.budget && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ color: GOLD, border: `1px solid rgba(193,154,107,0.3)` }}>{s.budget}</span>}
                    {(s.secteurs || []).slice(0, 2).map((x, i) => <span key={i} className="text-[10px] text-zinc-400 border border-white/10 rounded-full px-2 py-0.5">{x}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {err && <p className="text-sm text-red-400 mt-3" data-testid="acq-error">{err}</p>}
    </div>
  ) : (
    <div data-testid="acq-auth-panel">
      <div className="flex gap-2 mb-3.5" data-testid="acq-mode-switch">
        {modePill("account", <UserPlus className="w-3.5 h-3.5" />, t("acqspace.account_toggle"))}
        {modePill("guest", <ShieldCheck className="w-3.5 h-3.5" />, t("acqspace.guest_toggle"))}
      </div>

      {mode === "account" && (
        <>
          <button onClick={googleSignup} data-testid="acq-google" className="w-full flex items-center justify-center gap-3 bg-white text-[#1a1a1a] font-medium text-sm py-3 rounded-xl hover:bg-zinc-100 transition-colors">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> {t("acqspace.google_cta")}
          </button>
          <div className="flex items-center gap-3 text-xs text-zinc-500 my-3"><div className="flex-1 h-px bg-white/10" /> {t("acqspace.or_email")} <div className="flex-1 h-px bg-white/10" /></div>
        </>
      )}

      <div className="grid sm:grid-cols-2 gap-2">
        <input className={inp} placeholder={t("acqspace.fn") + (mode === "guest" ? " *" : "")} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="acq-first" autoComplete="given-name" />
        <input className={inp} placeholder={t("acqspace.ln")} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="acq-last" autoComplete="family-name" />
        <input className={inp} type="email" inputMode="email" autoComplete="email" placeholder={t("acqspace.email") + " *"} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="acq-email" />
        <input className={inp} type="tel" inputMode="tel" autoComplete="tel" placeholder={t("acqspace.phone")} value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="acq-phone" />
        {mode === "account" && (
          <input className={`${inp} sm:col-span-2`} type="password" autoComplete="new-password" placeholder={t("acqspace.password") + " * (6+)"} value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="acq-password" />
        )}
      </div>

      {mode === "guest" && (
        <label className="flex items-start gap-2.5 text-xs text-zinc-300 mt-3 cursor-pointer rounded-xl border border-white/10 px-3.5 py-3" data-testid="acq-anon-label">
          <input type="checkbox" checked={f.anonymous} onChange={(e) => set("anonymous", e.target.checked)} data-testid="acq-anonymous" className="accent-[#C19A6B] w-4 h-4 mt-0.5" />
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 shrink-0" style={{ color: GOLD }} /> {t("acqspace.anon_label")} <span className="text-zinc-500">{t("acqspace.anon_hint")}</span></span>
        </label>
      )}

      {err && <p className="text-sm text-red-400 mt-3" data-testid="acq-error">{err}</p>}

      <p className="text-center text-[11px] text-zinc-500 mt-3">{t("acqspace.reassure")}</p>
      <p className="text-center text-sm text-zinc-400 mt-2">{t("acqspace.already")} <Link to="/connexion" className="text-[#C19A6B] hover:underline inline-flex items-center gap-1"><LogIn className="w-3.5 h-3.5" /> {t("acqspace.login")}</Link></p>
    </div>
  );

  const accountCta = {
    label: isAuthed ? t("acqspace.save_cta") : t("acqspace.create_cta"),
    onSubmit: submit,
    busy: loading,
    disabled: (f.raw_text || "").trim().length < 10,
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-28 pb-20" data-testid="acquereur-done-wrap">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center border border-white/10 bg-[#0d0d10] rounded-2xl p-8 sm:p-10 max-w-lg w-full" data-testid="acquereur-done">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(193,154,107,0.15)" }}><Check className="w-7 h-7" style={{ color: GOLD }} /></div>
          <h1 className="text-2xl font-light text-white">{t("acqspace.done_title", { name: done.display_name || "" })}</h1>
          <p className="text-sm text-zinc-400 mt-3">{t("acqspace.done_text")}</p>
          {done.summary && <p className="text-sm text-zinc-300 italic mt-4 border-l-2 pl-4 text-left" style={{ borderColor: GOLD }}>« {done.summary} »</p>}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <Link to="/" className="inline-flex items-center gap-2 text-[#050505] font-medium uppercase tracking-wider text-sm px-6 py-3 rounded-full transition-colors" style={{ background: GOLD }}><Home className="w-4 h-4" /> {t("acqspace.home")}</Link>
            <button onClick={() => { setDone(null); setF((s) => ({ ...s, raw_text: "" })); }} data-testid="acq-add-another" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#C19A6B] text-zinc-200 hover:text-[#C19A6B] text-sm font-medium uppercase tracking-wider px-6 py-3 rounded-full transition-colors">{t("acqspace.new_search")}</button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <EspaceAcquereur
        layout="hero"
        slug={slug}
        brand={brand}
        value={f.raw_text}
        onChange={(v) => set("raw_text", v)}
        accountPanel={AccountPanel}
        accountCta={accountCta}
        connected={isAuthed}
      />
      {isAuthed && matches.length > 0 && (
        <section className="bg-[#050505] px-5 sm:px-6 py-12" data-testid="acq-matches">
          <div className="mx-auto" style={{ maxWidth: "min(90vw, 1200px)" }}>
            <p className="text-xs uppercase tracking-widest mb-1 flex items-center gap-2" style={{ color: GOLD }}><Sparkles className="w-3.5 h-3.5" /> {t("acqspace.matches_title")}</p>
            <p className="text-sm text-zinc-500 mb-5">{t("acqspace.matches_sub")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {matches.map((l, i) => (
                <div key={l.slug} className="relative">
                  <span className="absolute z-10 top-11 left-3 text-[10px] uppercase tracking-wider rounded-full text-white px-2.5 py-1 shadow-lg" style={{ background: GOLD }} data-testid="acq-match-badge">{t("acqspace.match_pct", { pct: l.match_score })}</span>
                  <ListingCard listing={l} index={i} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
